import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import API from '../../api/drafts';
import { throwErrorMessage } from '../utils/api';

export const state = {
  records: [],
  scheduled: [],
  inConversations: {},
  uiFlags: {
    isFetchingItem: false,
    isFetching: false,
    isCreating: false,
    isDeleting: false,
    isUpdating: false,
  },
};

export const getters = {
  getDrafts($state) {
    return $state.records;
  },
  getScheduled($state) {
    return $state.scheduled;
  },
  getDraftsByConversation: $state => displayId => {
    return $state.inConversations[displayId] || [];
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
};

export const actions = {
  get: async function getDrafts({ commit }) {
    commit(types.SET_DRAFTS_UI_FLAG, { isFetching: true });
    try {
      const response = await API.get();
      commit(types.SET_DRAFTS, response.data);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_DRAFTS_UI_FLAG, { isFetching: false });
    }
  },
  getScheduled: async function getScheduled({ commit }) {
    commit(types.SET_DRAFTS_UI_FLAG, { isFetching: true });
    try {
      const response = await API.getScheduled();
      commit(types.SET_SCHEDULED, response.data);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_DRAFTS_UI_FLAG, { isFetching: false });
    }
  },
  cancelScheduled: async ({ commit }, draftId) => {
    commit(types.SET_DRAFTS_UI_FLAG, { isUpdating: true });
    try {
      const response = await API.cancelScheduled(draftId);
      commit(types.EDIT_DRAFT, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      return {};
    } finally {
      commit(types.SET_DRAFTS_UI_FLAG, { isUpdating: false });
    }
  },
  getSingle: async function getDraftById({ commit }, draftId) {
    commit(types.SET_DRAFTS_UI_FLAG, { isFetchingItem: true });
    try {
      const response = await API.show(draftId);
      commit(types.ADD_DRAFT, response.data);
      return response.data;
    } catch (error) {
      return {};
    } finally {
      commit(types.SET_DRAFTS_UI_FLAG, { isFetchingItem: false });
    }
  },
  inConversation: async function inConversation(
    { commit },
    { displayId, contactId }
  ) {
    commit(types.SET_DRAFTS_UI_FLAG, { isFetching: true });
    const query = { contact_id: contactId };
    try {
      const response = await API.inConversation(displayId, query);
      commit(types.CONVERSATION_DRAFTS, {
        displayId: displayId,
        data: response.data,
      });
      return response.data;
    } catch (error) {
      // Ignore error
      return [];
    } finally {
      commit(types.SET_DRAFTS_UI_FLAG, { isFetching: false });
    }
  },
  scheduleSend: async ({ commit }, { id, scheduledAt, timeZone }) => {
    commit(types.SET_DRAFTS_UI_FLAG, { isUpdating: true });
    try {
      const response = await API.scheduleSend(id, { timeZone, scheduledAt });
      commit(types.EDIT_DRAFT, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      return {};
    } finally {
      commit(types.SET_DRAFTS_UI_FLAG, { isUpdating: false });
    }
  },
  create: async ({ commit }, { ...initData }) => {
    commit(types.SET_DRAFTS_UI_FLAG, { isCreating: true });
    try {
      const response = await API.create(initData);
      commit(types.ADD_DRAFT, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      return {};
    } finally {
      commit(types.SET_DRAFTS_UI_FLAG, { isCreating: false });
    }
  },
  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_DRAFTS_UI_FLAG, { isUpdating: true });
    try {
      const response = await API.update(id, updateObj);
      commit(types.EDIT_DRAFT, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      return {};
    } finally {
      commit(types.SET_DRAFTS_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_DRAFTS_UI_FLAG, { isDeleting: true });
    try {
      await API.delete(id);
      commit(types.DELETE_DRAFT, id);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_DRAFTS_UI_FLAG, { isDeleting: false });
    }
  },
  attachFiles: async ({ commit }, { draftId, files }) => {
    commit(types.SET_DRAFTS_UI_FLAG, { isUpdating: true });
    try {
      const response = await API.attachFiles(draftId, files);
      commit(types.EDIT_DRAFT, response.data);
      return response.data?.files || [];
    } catch (error) {
      throwErrorMessage(error);
      return {};
    } finally {
      commit(types.SET_DRAFTS_UI_FLAG, { isUpdating: false });
    }
  },
  removeFile: async ({ commit }, { draftId, fileId }) => {
    commit(types.SET_DRAFTS_UI_FLAG, { isUpdating: true });
    try {
      const response = await API.removeFile(draftId, fileId);
      commit(types.EDIT_DRAFT, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      return {};
    } finally {
      commit(types.SET_DRAFTS_UI_FLAG, { isUpdating: false });
    }
  },
};

export const mutations = {
  [types.SET_DRAFTS_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.ADD_DRAFT]: MutationHelpers.setSingleRecord,
  [types.SET_DRAFTS]: MutationHelpers.set,
  [types.EDIT_DRAFT]: MutationHelpers.update,
  [types.DELETE_DRAFT]: MutationHelpers.destroy,
  [types.CONVERSATION_DRAFTS]($state, { displayId, data }) {
    $state.inConversations[displayId] = data;
  },
  [types.SET_SCHEDULED]($state, data) {
    $state.scheduled = data;
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
